import React, { useState, useRef, useCallback, useEffect } from 'react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { type Editor } from '@ckeditor/ckeditor5-core';

import DOMPurify from 'dompurify';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { Backdrop, Box, IconButton, Stack, Typography } from '@mui/material';
import { CloseFullscreen, Minimize, OpenInFull } from '@mui/icons-material';

import { Channels } from '@bandido/shared/constants';

import { QueryKeys } from '@frontend/features/common/types';
import { useGetMyUserData } from '@frontend/features/auth/queries';
import { createUploadAdapterPlugin } from '@frontend/features/common/helper';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { NewCaseHistoryDto, useCreateCaseHistoryItem, useGetCaseById, useReplyToEmail } from '../../queries';
import { useEditor } from '../../hooks/useEditor';
import { useAttachments } from './hooks/useAttachments';
import { useEditorActions } from './hooks/useEditorActions';
import { FormData, CKEditorInstance, NoteEditorComposeState, NoteEditorSuggestedDraft } from './types';
import { emailDraftHtml } from './emailDraftHtml';
import { EditorContainer, FloatingWindow, Form } from './styled';
import { Toolbar } from './components/Toolbar';
import { AttachmentsList } from './components/AttachmentsList';
import { EmailForm } from './components/EmailForm';
import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { MappedFile, SettingsFields } from '@bandido/shared/types';
import { useDraft } from '../../context/DraftContext';

interface Props {
  onMinimize: () => void;
  onClose: () => void;
  caseId?: number;
  onSent?: () => void;
  initialComposeState?: NoteEditorComposeState;
  onComposeStateChange?: (state: NoteEditorComposeState) => void;
  isSubmitBlocked?: boolean;
  onSendStart?: () => void;
  onSendError?: () => void;
  closeAfterSent?: boolean;
  showFullscreenControl?: boolean;
  embedded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  suggestedDraft?: NoteEditorSuggestedDraft;
  onDraftReadyChange?: (ready: boolean) => void;
}

export const NoteEditor = React.memo(({
  onMinimize,
  onClose,
  caseId,
  onSent,
  initialComposeState,
  onComposeStateChange,
  isSubmitBlocked,
  onSendStart,
  onSendError,
  closeAfterSent = true,
  showFullscreenControl = true,
  embedded = false,
  onExpandedChange,
  suggestedDraft,
  onDraftReadyChange,
}: Props) => {
  const editorRef = useRef<CKEditorInstance>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editorData, setEditorData] = useState(initialComposeState?.content ?? '');

  const [isFullScreen, setIsFullScreen] = useState(false);
  const { attachments, handleFileChange, handleRemoveAttachment } = useAttachments(initialComposeState?.attachments);
  const { addToast } = useSnackBar();

  const { channel, historyIdToReply, subject, to, cc, bcc, editorContent } = useEditor();
  const queryClient = useQueryClient();

  const { id: routeCaseId } = useParams<{ id: string }>();
  const resolvedCaseId = caseId ?? Number(routeCaseId);

  const { draft, setDraft } = useDraft();

  const { t } = useTranslation();

  const { data: { data: user } = {} } = useGetMyUserData();

  const { data: caseData } = useGetCaseById({ id: resolvedCaseId });

  const { data: settings } = useGetSettingsByAgencyId({
    id: Number(caseData?.agencyId),
  });

  const methods = useForm<FormData>({
    defaultValues: initialComposeState?.form ?? {
      to: [],
      cc: [],
      bcc: [],
      from: '',
      subject: '',
      content: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (!onComposeStateChange) return;
    onComposeStateChange({ form: methods.getValues(), content: editorData, attachments, historyId: historyIdToReply });
  }, [attachments, editorData, methods, onComposeStateChange, historyIdToReply]);

  useEffect(() => {
    if (!onComposeStateChange) return;
    const subscription = methods.watch((form) => {
      onComposeStateChange({ form: form as FormData, content: editorData, attachments, historyId: historyIdToReply });
    });
    return () => subscription.unsubscribe();
  }, [attachments, editorData, methods, onComposeStateChange, historyIdToReply]);

  useEffect(() => {
    if (!historyIdToReply) return;
    if (initialComposeState?.historyId === historyIdToReply) return;
    reset({
      to,
      cc,
      bcc,
      subject,
      content: '',
    });
  }, [historyIdToReply, to, subject, reset, cc, bcc]);

  const replaceSignaturePlaceholders = useCallback(
    (signature: string) => {
      if (!user) return signature;

      if (!signature) return '';

      const userObj = {
        ...user,
        fullname: `${user.firstName} ${user.lastName}`,
      };
      const signatureWithPlaceholders = signature?.replace(
        /{{(\w+)}}/g,
        (_: string, key: string) => String(userObj[key as keyof typeof userObj]) || '',
      );
      return signatureWithPlaceholders;
    },
    [user],
  );

  useEffect(() => {
    if (!settings) return;

    setEditorData((prevEditorData) => {
      let nextEditorContent = '';

      const draftContent = draft[channel as Channels.EMAIL | Channels.NOTES];
      if (draftContent) return draftContent;

      const signature = replaceSignaturePlaceholders(settings[SettingsFields.EMAIL_SIGNATURE]);
      const hasSignature = prevEditorData?.includes(signature);

      if (hasSignature) {
        nextEditorContent = channel === Channels.EMAIL ? prevEditorData : '';
      } else {
        nextEditorContent = channel === Channels.EMAIL ? signature : '';
      }

      if (editorContent) {
        nextEditorContent = channel === Channels.EMAIL ? `${signature} <br/> ${editorContent}` : editorContent;
      }

      return nextEditorContent;
    });
  }, [settings, replaceSignaturePlaceholders, channel, editorContent]);

  useEffect(() => {
    onDraftReadyChange?.(Boolean(settings) && !isSubmitBlocked);
    return () => onDraftReadyChange?.(false);
  }, [settings, isSubmitBlocked, onDraftReadyChange]);

  const appliedDraft = useRef<number>();
  useEffect(() => {
    if (!suggestedDraft || appliedDraft.current === suggestedDraft.id || !settings || isSubmitBlocked) return;
    appliedDraft.current = suggestedDraft.id;
    const html = emailDraftHtml(suggestedDraft.text, replaceSignaturePlaceholders(settings[SettingsFields.EMAIL_SIGNATURE]));
    setEditorData(html);
    if (!historyIdToReply) methods.setValue('subject', suggestedDraft.subject);
    setDraft((previous) => ({ ...previous, [Channels.EMAIL]: html }));
    editorRef.current.editor?.editing.view.focus();
  }, [suggestedDraft, settings, isSubmitBlocked, replaceSignaturePlaceholders, historyIdToReply, methods, setDraft]);

  const { mutate: createCaseHistoryItem, isLoading: isSavingNewItem } = useCreateCaseHistoryItem({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCaseHistory]);
      onSent?.();
      if (closeAfterSent) onClose();
    },
    onError: onSendError,
  });

  const { mutate: replyToEmail, isLoading: isReplyingToEmail } = useReplyToEmail(Number(historyIdToReply), {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getCaseHistory]);
      onSent?.();
      if (closeAfterSent) onClose();
    },
    onError: onSendError,
  });

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    onExpandedChange?.(!isFullScreen);
  };

  const { handleFocus, handleUndo, handleRedo, handleBold, handleItalic, handleUnderline, handleBullets } =
    useEditorActions(editorRef);

  const handleAttachment = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleEditorChange = useCallback(
    (_: any, editor: Editor) => {
      const data = editor.getData();
      setEditorData(data);
      if (!channel) return;

      setDraft((prev) => ({ ...prev, [channel]: data }));
    },
    [channel, setDraft],
  );

  const extractNewContent = (htmlContent: string): string => {
    const regex = /([\s\S]*?)(?=<blockquote><p><strong>[^:]*:<\/strong>\s*[^<]*@[^<]*<\/p>)/i;
    const match = htmlContent.match(regex);

    if (match && match[1]) {
      const extractedContent = match[1].trim();
      const cleanContent = extractedContent
        .replace(/<p><br\s*\/?>&nbsp;<\/p>\s*$/gi, '')
        .replace(/<p><br\s*\/?><\/p>\s*$/gi, '')
        .trim();
      return cleanContent || '<p></p>';
    }

    return htmlContent;
  };

  const onSubmit = handleSubmit((data: FormData) => {
    if (isSubmitBlocked) return;
    onSendStart?.();
    const purifiedContent = DOMPurify.sanitize(editorData) || '<p></p>';

    const finalContent = historyIdToReply ? extractNewContent(purifiedContent) : purifiedContent;

    const payload: NewCaseHistoryDto = {
      channel: channel === Channels.EMAIL ? Channels.EMAIL : Channels.NOTES,
      caseId: resolvedCaseId,
      attachments: attachments as unknown as MappedFile[],
      [channel === Channels.EMAIL ? 'email' : 'note']: {
        subject: data.subject,
        content: finalContent,
        ...(channel === Channels.EMAIL && {
          originalContent: finalContent,
          originalText: finalContent,
          to: data.to.map((mail) => mail.email),
          cc: data.cc.map((mail) => mail.email),
          bcc: data.bcc.map((mail) => mail.email),
          from: data.from,
        }),
      },
    };

    if (historyIdToReply) {
      replyToEmail(payload);
    } else {
      createCaseHistoryItem(payload);
    }
  });

  const isLoading = isSavingNewItem || isReplyingToEmail || Boolean(isSubmitBlocked);

  const handleToast = useCallback(
    (message: string) => {
      addToast({
        message,
        color: 'error',
      });
    },
    [addToast],
  );

  const editorTitle = channel === Channels.EMAIL ? t('noteEditor.emailTitle') : t('noteEditor.noteTitle');
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <>
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={!embedded && isFullScreen}
        onClick={toggleFullScreen}
      />

      <FloatingWindow elevation={embedded ? 0 : 3} fullscreen={isFullScreen} embedded={embedded}>
        <FormProvider {...methods}>
          <Stack
            sx={{
              svg: {
                width: 20,
                height: 20,
              },
            }}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            paddingBottom={embedded ? 1 : 3}
            flexShrink={embedded ? 0 : undefined}
            gap={1}
            width="100%"
          >
            <Typography variant="h6">{editorTitle}</Typography>
            <Stack flexDirection="row" gap={1}>
              {showFullscreenControl && (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={toggleFullScreen}
                  aria-label={t('noteEditor.controls.maximize')}
                >
                  {isFullScreen ? <CloseFullscreen /> : <OpenInFull />}
                </IconButton>
              )}
              <IconButton size="small" color="primary" onClick={onMinimize} aria-label={t('noteEditor.controls.close')}>
                <Minimize />
              </IconButton>
            </Stack>
          </Stack>

          <Form
            isNote={channel === Channels.NOTES}
            onSubmit={onSubmit}
            sx={embedded ? {
              minHeight: 0,
              '& > *': { flexShrink: 0 },
              '& > .MuiToolbar-root': { flexWrap: 'wrap', gap: 1 },
            } : undefined}
          >
            {channel === Channels.EMAIL && (
              <Stack sx={{ pb: 2 }}>
                <EmailForm agencyId={caseData?.agencyId} />
              </Stack>
            )}

            <input
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="attachment-input"
              type="file"
              onChange={handleFileChange}
            />

            <EditorContainer fullscreen={isFullScreen} onClick={handleFocus}>
              <CKEditor
                editor={ClassicEditor}
                data={editorData}
                onReady={(editor: Editor) => {
                  editorRef.current.editor = editor;
                }}
                onChange={handleEditorChange}
                config={{
                  placeholder: t('noteEditor.placeholder'),
                  extraPlugins: [createUploadAdapterPlugin(handleToast)],
                  toolbar: [],
                }}
              />
              {errors.content && (
                <Typography color="error" variant="caption">
                  {errors.content.message}
                </Typography>
              )}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
              >
                <AttachmentsList attachments={attachments} onRemoveAttachment={handleRemoveAttachment} />
              </Box>
            </EditorContainer>
            <Toolbar
              onUndo={handleUndo}
              onRedo={handleRedo}
              onBold={handleBold}
              onItalic={handleItalic}
              onUnderline={handleUnderline}
              onBullets={handleBullets}
              onAttachment={handleAttachment}
              isLoading={isLoading}
              disabled={(channel === Channels.EMAIL && !user) || isLoading || hasErrors}
            />
          </Form>
        </FormProvider>
      </FloatingWindow>
    </>
  );
});

NoteEditor.displayName = 'NoteEditor';
